import { Action, Selector, State, StateContext } from '@ngxs/store';
import { BehaviorSubject, filter, Observable, of, Subject, switchMap } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { NotificationService } from '../../../../../common';
import {
  ShipmentGlobalTrackingMapLoad,
  ShipmentGlobalTrackingMapSetInitialState,
} from './shipment-global-tracking-map.actions';
import { ShipmentGlobalTrackingMapService } from './shipment-global-tracking-map.service';
import { ShipmentGlobalTrackingMapModel } from '../models/shipment-list/shipment-global-tracking-map.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Injectable } from '@angular/core';
import { isNil } from 'lodash';
import { ShipmentLoadToGpsPointModel } from '../../../../models';

export type ShipmentGlobalTrackingMapStateModel = {
  firstChunkLoaded: boolean;
  completelyLoaded: boolean;
  shipments: ShipmentGlobalTrackingMapModel[];
  totalShipments: number;
};

const initialState: ShipmentGlobalTrackingMapStateModel = {
  firstChunkLoaded: false,
  completelyLoaded: false,
  shipments: [],
  totalShipments: 0,
};

@State<ShipmentGlobalTrackingMapStateModel>({
  name: 'shipmentsMap',
  defaults: initialState,
})
@Injectable()
export class ShipmentGlobalTrackingMapState {
  private mapRequestsQueueUnsubscribe$: Subject<void> = new Subject();

  constructor(
    private shipmentGlobalTrackingMapService: ShipmentGlobalTrackingMapService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getFirstChunkLoaded(state: ShipmentGlobalTrackingMapStateModel): boolean {
    return state.firstChunkLoaded;
  }

  @Selector()
  static getCompletelyLoaded(state: ShipmentGlobalTrackingMapStateModel): boolean {
    return state.completelyLoaded;
  }

  @Selector()
  static getShipments(state: ShipmentGlobalTrackingMapStateModel): ShipmentGlobalTrackingMapModel[] {
    return state.shipments;
  }

  @Selector()
  static getTotalShipments(state: ShipmentGlobalTrackingMapStateModel): number {
    return state.totalShipments;
  }

  @Action(ShipmentGlobalTrackingMapLoad)
  getShipmentList(
    ctx: StateContext<ShipmentGlobalTrackingMapStateModel>,
    { searchCriteria, mapThreshold }: ShipmentGlobalTrackingMapLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const SHIPMENTS_PER_PAGE = 20;
    const MAXIMUM_PAGE_NUMBER = Math.ceil(mapThreshold / SHIPMENTS_PER_PAGE);
    const currentPage$: BehaviorSubject<number> = new BehaviorSubject(0);
    let currentPage = 0;
    let totalShipments = 0;
    let shipmentSortIndex = 0;
    let shipmentPointSortIndex = 0;
    let shipmentsWithNoEmptyCoordinates: ShipmentGlobalTrackingMapModel[] | null = null;

    this.mapRequestsQueueUnsubscribe$.next();
    ctx.patchState({ firstChunkLoaded: false, completelyLoaded: false });

    return currentPage$.pipe(
      filter(() => {
        if (shipmentsWithNoEmptyCoordinates?.length === 0) {
          ctx.patchState({ completelyLoaded: true });
        }

        return shipmentsWithNoEmptyCoordinates === null || !!shipmentsWithNoEmptyCoordinates.length;
      }),
      takeUntil(this.mapRequestsQueueUnsubscribe$),
      switchMap((page: number) => {
        const size =
          mapThreshold >= SHIPMENTS_PER_PAGE * (currentPage + 1)
            ? SHIPMENTS_PER_PAGE
            : mapThreshold % SHIPMENTS_PER_PAGE;

        return this.shipmentGlobalTrackingMapService
          .getShipmentMapData({
            ...searchCriteria,
            page,
            size,
          })
          .pipe(
            takeUntil(this.mapRequestsQueueUnsubscribe$),
            map((shipments: ShipmentGlobalTrackingMapModel[]) => {
              shipmentsWithNoEmptyCoordinates = shipments.reduce(
                (acc: ShipmentGlobalTrackingMapModel[], shipment: ShipmentGlobalTrackingMapModel) => {
                  if (!isNil(shipment.position.longitude) && !isNil(shipment.position.latitude)) {
                    shipment.sortIndex = `${shipmentSortIndex}`;
                    shipment.gpsPoints = shipment.gpsPoints.map((point: ShipmentLoadToGpsPointModel) => {
                      // TODO: temporary solution with sortIndex. discuss with BE team
                      point.sortIndex = `${shipmentSortIndex}_${shipmentPointSortIndex}`;

                      shipmentPointSortIndex += 1;
                      return point;
                    });

                    acc.push(shipment);

                    shipmentSortIndex += 1;

                    return acc;
                  }

                  return acc;
                },
                [],
              );

              totalShipments += shipmentsWithNoEmptyCoordinates.length;

              ctx.patchState({
                shipments: shipmentsWithNoEmptyCoordinates,
                firstChunkLoaded: currentPage >= 0,
                completelyLoaded: currentPage === MAXIMUM_PAGE_NUMBER - 1,
                totalShipments,
              });
            }),
            catchError(({ error }: CustomHttpErrorResponse<Error>) =>
              of(this.notificationService.error(error.message)),
            ),
            finalize(() => {
              currentPage++;
              if (currentPage < MAXIMUM_PAGE_NUMBER) {
                currentPage$.next(currentPage);
              }
            }),
          );
      }),
    );
  }

  @Action(ShipmentGlobalTrackingMapSetInitialState)
  setInitialState(ctx: StateContext<ShipmentGlobalTrackingMapStateModel>): void {
    this.mapRequestsQueueUnsubscribe$.next();
    ctx.setState(initialState);
  }
}
